import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import _request from "actions/_request";
import _helper from "actions/_helper";
import i18n from "i18";
import { buildConnectionCommands } from "utils/mcpCommands";

const EXPIRY_DAYS = [7, 30, 90];
const CLIENTS = ["claudeCode", "codex", "claudeDesktop"];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

/**
 * Profile tab: create, copy and revoke personal API tokens, and show the command that
 * connects Claude Code / Codex / Claude Desktop to this Connexxtion account.
 * The secret is kept only in component state and only until the user dismisses it.
 * @returns {JSX.Element} The tab content
 */
export default function AiAssistantsTab() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [canPublish, setCanPublish] = useState(true);
  const [days, setDays] = useState(30);
  const [nameError, setNameError] = useState(false);
  const [created, setCreated] = useState(null);
  const [client, setClient] = useState(0);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const resp = await _request.apiTokens.list();
    if (resp.isOk) setTokens(resp.data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    const resp = await _request.apiTokens.create({
      name: name.trim(),
      abilities: canPublish ? ["read", "publish"] : ["read"],
      days,
    });
    if (!resp.isOk) return;

    setCreated(resp.data);
    setCopied(false);
    setClient(0);
    setName("");
    load();
  };

  const revoke = (token) => {
    _helper.confirm(
      `^${i18n.t("aiAssistants.revokeConfirm", { name: token.name })}`,
      async () => {
        const resp = await _request.apiTokens.revoke(token.id);
        if (resp.isOk) {
          if (created?.id === token.id) setCreated(null);
          load();
        }
      },
    );
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const commands = created
    ? buildConnectionCommands({ token: created.token, apiUrl: `${window.location.origin}/api` })
    : null;
  const command = commands ? commands[CLIENTS[client]] : "";

  return (
    <Box sx={{ maxWidth: 760, color: "var(--text-color)" }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {i18n.t("aiAssistants.title")}
      </Typography>
      <Typography sx={{ mb: 3, opacity: 0.8 }}>{i18n.t("aiAssistants.intro")}</Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start", mb: 1 }}>
        <TextField
          size="small"
          label={i18n.t("aiAssistants.tokenName")}
          placeholder={i18n.t("aiAssistants.tokenNamePlaceholder")}
          value={name}
          error={nameError}
          helperText={nameError ? i18n.t("aiAssistants.nameRequired") : ""}
          inputProps={{ maxLength: 64 }}
          onChange={(e) => {
            setName(e.target.value);
            setNameError(false);
          }}
          sx={{ minWidth: 260 }}
        />
        <Select
          size="small"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          inputProps={{ "aria-label": i18n.t("aiAssistants.expires") }}>
          {EXPIRY_DAYS.map((d) => (
            <MenuItem key={d} value={d}>
              {i18n.t("aiAssistants.days", { count: d })}
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" onClick={create}>
          {i18n.t("aiAssistants.create")}
        </Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <FormControlLabel control={<Checkbox checked disabled />} label={i18n.t("aiAssistants.read")} />
        <FormControlLabel
          control={<Checkbox checked={canPublish} onChange={(e) => setCanPublish(e.target.checked)} />}
          label={i18n.t("aiAssistants.publish")}
        />
      </Box>

      {created && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setCreated(null)}>
          <Typography sx={{ fontWeight: 500, mb: 1 }}>{i18n.t("aiAssistants.secretShown")}</Typography>
          <Tabs value={client} onChange={(_, v) => { setClient(v); setCopied(false); }} sx={{ mb: 1 }}>
            {CLIENTS.map((c) => (
              <Tab key={c} label={i18n.t(`aiAssistants.${c}`)} />
            ))}
          </Tabs>
          <Box
            component="pre"
            data-testid="connection-command"
            sx={{ m: 0, p: 1.5, bgcolor: "rgba(0,0,0,0.06)", borderRadius: 1, overflowX: "auto", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {command}
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
            <Button size="small" variant="outlined" onClick={() => copy(command)}>
              {copied ? i18n.t("aiAssistants.copied") : i18n.t("aiAssistants.copy")}
            </Button>
            <Button size="small" onClick={() => copy(created.token)}>
              {i18n.t("aiAssistants.copyToken")}
            </Button>
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: 13 }}>{i18n.t("aiAssistants.warning")}</Typography>
        </Alert>
      )}

      {tokens.length === 0 ? (
        <Typography sx={{ opacity: 0.7 }}>{i18n.t("aiAssistants.empty")}</Typography>
      ) : (
        tokens.map((token) => (
          <Box
            key={token.id}
            data-testid="token-row"
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, py: 1.5, borderTop: "1px solid var(--border-color, rgba(128,128,128,0.25))" }}>
            <Box>
              <Typography sx={{ fontWeight: 500 }}>{token.name}</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
                {(token.abilities || []).map((a) => i18n.t(`aiAssistants.${a}Short`)).join(", ")}
                {" · "}
                {token.last_used_at
                  ? i18n.t("aiAssistants.lastUsed", { date: formatDate(token.last_used_at) })
                  : i18n.t("aiAssistants.neverUsed")}
                {" · "}
                {i18n.t("aiAssistants.expiresOn", { date: formatDate(token.expires_at) })}
              </Typography>
            </Box>
            <Button color="error" size="small" onClick={() => revoke(token)}>
              {i18n.t("aiAssistants.revoke")}
            </Button>
          </Box>
        ))
      )}
    </Box>
  );
}
